/**
 * ContactModal Configuration
 * Config-based component for displaying Loan Officer or Branch contact information
 * Shown when user clicks on Contact button in the header
 */

import React from 'react';
import { Box, Modal as MuiModal } from '@mui/material';
import { Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {
  Typography,
  RowLayout,
  ColumnLayout,
  IconButton,
} from '@/components/atoms';
import type { MappedLoanOfficer } from '@/services/mappers/loanOfficer.mapper';

interface BranchData {
  branchId: string;
  name: string;
  address?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  branchNmls?: string;
  websiteUrl?: string;
  logo?: string;
  photo?: string | null;
}

export interface ContactModalConfigParams {
  theme: Theme;
  open: boolean;
  onClose: () => void;
  loanOfficerData?: MappedLoanOfficer | null;
  branchData?: BranchData | null;
  /** Callback for clipboard operations - receives success status and optional error message */
  onCopyToClipboard?: (success: boolean, errorMessage?: string) => void;
}

/**
 * Truncate website URL for display
 */
const truncateWebsiteUrl = (url: string, maxLength = 35): string => {
  if (!url) return '';
  // Remove protocol
  let displayUrl = url.replace(/^https?:\/\//, '');
  // Remove trailing slash
  displayUrl = displayUrl.replace(/\/$/, '');
  // Truncate if too long
  if (displayUrl.length > maxLength) {
    return displayUrl.substring(0, maxLength) + '...';
  }
  return displayUrl;
};

/**
 * Copy text to clipboard with optional callback for user feedback
 */
const copyToClipboard = async (
  text: string,
  onCopy?: (success: boolean, errorMessage?: string) => void
) => {
  try {
    await navigator.clipboard.writeText(text);
    onCopy?.(true);
  } catch (err) {
    console.error('Failed to copy:', err);
    const errorMessage =
      err instanceof Error ? err.message : 'Failed to copy to clipboard';
    onCopy?.(false, errorMessage);
  }
};

/**
 * Get ContactModal configuration
 * Returns a React element that can be rendered directly
 */
export const getContactModalConfig = ({
  theme,
  open,
  onClose,
  loanOfficerData,
  branchData,
  onCopyToClipboard,
}: ContactModalConfigParams): React.ReactNode => {
  // Determine what data to display (loan officer takes precedence)
  const isLoanOfficer = !!loanOfficerData;
  const tealBackground =
    theme.palette['Color Selection']?.Teal?.['Teal 200'] || '#41C6CE';

  // Extract display values
  const name = isLoanOfficer
    ? loanOfficerData?.name || ''
    : branchData?.name || '';
  const title = isLoanOfficer
    ? loanOfficerData?.title || 'Loan Officer'
    : 'Branch';
  const nmls = isLoanOfficer
    ? loanOfficerData?.nmls || ''
    : branchData?.branchNmls?.replace('#', '') || '';
  const phone = isLoanOfficer
    ? loanOfficerData?.phone || ''
    : branchData?.phone || '';
  const email = isLoanOfficer
    ? loanOfficerData?.email || ''
    : branchData?.email || '';
  // For branches, prioritize logo as the display image
  const photo = isLoanOfficer
    ? loanOfficerData?.photo || undefined
    : branchData?.logo || branchData?.photo || undefined;

  // Default website URL
  const defaultWebsiteUrl = 'https://www.revolutionmortgage.com/';

  // Get website URL - use landingPageUrl as fallback if websiteUrl is null
  const fullWebsiteUrl = isLoanOfficer
    ? loanOfficerData?.websiteUrl ||
      loanOfficerData?.landingPageUrl ||
      defaultWebsiteUrl
    : branchData?.websiteUrl || defaultWebsiteUrl;

  const displayWebsiteUrl = truncateWebsiteUrl(fullWebsiteUrl);

  // Build address string - format as two lines for display
  const addressLine1 = isLoanOfficer
    ? loanOfficerData?.fullAddress?.split(',')[0] || ''
    : branchData?.addressLine || branchData?.address?.split(',')[0] || '';

  const addressLine2 = isLoanOfficer
    ? loanOfficerData?.city &&
      loanOfficerData?.state &&
      loanOfficerData?.zipCode
      ? `${loanOfficerData.city}, ${loanOfficerData.state} ${loanOfficerData.zipCode}`
      : ''
    : branchData?.city && branchData?.state && branchData?.zipCode
      ? `${branchData.city}, ${branchData.state} ${branchData.zipCode}`
      : '';

  // Info field component with copy button
  const InfoField = ({
    label,
    value,
    copyValue,
    isLink = false,
    width = 280,
  }: {
    label: string;
    value: string;
    copyValue?: string;
    isLink?: boolean;
    width?: number;
  }) => (
    <ColumnLayout
      sx={{
        gap: theme.unit(1),
        width,
        minHeight: 44,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          ...theme.typography.M3.body.mediumProminent,
          color: theme.palette.M3?.sys?.light?.['on-surface-variant'],
          lineHeight: '20px',
        }}
      >
        {label}
      </Typography>
      <RowLayout alignItems="flex-start" sx={{ gap: theme.unit(4) }}>
        <Typography
          sx={{
            ...theme.typography.M3.body.medium,
            color: isLink
              ? theme.palette.M3?.sys?.light?.['on-primary-container']
              : theme.palette.M3?.sys?.light?.['on-surface'],
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            flex: 1,
          }}
        >
          {value}
        </Typography>
        <IconButton
          onClick={() => copyToClipboard(copyValue || value, onCopyToClipboard)}
          size="s"
          sx={{
            padding: 0,
            color: theme.palette.M3?.sys?.light?.outline,
            width: 16,
            height: 16,
            minWidth: 16,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </RowLayout>
    </ColumnLayout>
  );

  // Address field component with two-line display and copy button
  const AddressField = ({
    label,
    line1,
    line2,
    width = 257,
  }: {
    label: string;
    line1: string;
    line2: string;
    width?: number;
  }) => (
    <ColumnLayout
      sx={{
        gap: theme.unit(1),
        width,
        minHeight: 44,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          ...theme.typography.M3.body.mediumProminent,
          color: theme.palette.M3?.sys?.light?.['on-surface-variant'],
          lineHeight: '20px',
        }}
      >
        {label}
      </Typography>
      <RowLayout alignItems="flex-start" sx={{ gap: theme.unit(4) }}>
        <ColumnLayout sx={{ flex: 1, gap: 0 }}>
          <Typography
            sx={{
              ...theme.typography.M3.body.medium,
              color: theme.palette.M3?.sys?.light?.['on-surface'],
              wordBreak: 'break-word',
              lineHeight: '20px',
            }}
          >
            {line1}
          </Typography>
          <Typography
            sx={{
              ...theme.typography.M3.body.medium,
              color: theme.palette.M3?.sys?.light?.['on-surface'],
              wordBreak: 'break-word',
              lineHeight: '20px',
            }}
          >
            {line2}
          </Typography>
        </ColumnLayout>
        <IconButton
          onClick={() =>
            copyToClipboard(`${line1}, ${line2}`, onCopyToClipboard)
          }
          size="s"
          sx={{
            padding: 0,
            color: theme.palette.M3?.sys?.light?.outline,
            width: 16,
            height: 16,
            minWidth: 16,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </RowLayout>
    </ColumnLayout>
  );

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-labelledby="contact-modal-title"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(51, 57, 69, 0.4)',
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.M3?.white || '#FFFFFF',
          borderRadius: theme.unit(2.5),
          paddingTop: theme.unit(2.5),
          paddingBottom: theme.unit(7.5),
          paddingX: theme.unit(7.5),
          maxWidth: 787,
          width: '90%',
          outline: 'none',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: theme.unit(2),
          }}
        >
          <IconButton
            onClick={onClose}
            size="s"
            sx={{
              color: theme.palette.M3?.sys?.light?.['on-primary-fixed'],
              padding: theme.unit(2),
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <ColumnLayout sx={{ gap: theme.unit(5), paddingX: theme.unit(2.5) }}>
          {/* Header with Photo and Name */}
          <RowLayout alignItems="center" sx={{ gap: theme.unit(5.25) }}>
            {/* Profile Photo - Avatar container with positioned image */}
            <Box
              sx={{
                width: 91,
                height: 91,
                borderRadius: '100px',
                border: `0.4px solid ${theme.palette.M3?.sys?.light?.['outline 3']}`,
                backgroundColor: isLoanOfficer ? tealBackground : 'transparent',
                overflow: 'hidden',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              {photo && (
                <Box
                  component="img"
                  src={photo}
                  alt={name}
                  sx={
                    isLoanOfficer
                      ? {
                          position: 'absolute',
                          top: '2.77px',
                          left: '-0.4px',
                          width: 'calc(100% + 7.35%)',
                          height: 'auto',
                          objectFit: 'cover',
                        }
                      : {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }
                  }
                />
              )}
            </Box>

            {/* Name and Title */}
            <ColumnLayout sx={{ gap: theme.unit(2.5) }}>
              <Typography
                id="contact-modal-title"
                sx={{
                  ...theme.typography.M3.headline.large,
                  color: theme.palette.M3?.sys?.light?.['on-primary-fixed'],
                }}
              >
                {name}
              </Typography>
              <Typography
                sx={{
                  ...theme.typography.M3.body.large,
                  color: theme.palette.M3?.sys?.light?.['on-surface-variant'],
                }}
              >
                {`${title}, NMLS #${nmls}`}
              </Typography>
            </ColumnLayout>
          </RowLayout>

          {/* Contact Info Grid */}
          <Box sx={{ marginLeft: '112px' }}>
            {/* Row 1: Phone and Email */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: theme.unit(7.5),
                marginBottom: theme.unit(5),
              }}
            >
              <InfoField
                label="Phone No."
                value={phone || '(555) 555-0199'}
                width={280}
              />
              <InfoField
                label="Email ID"
                value={email || 'contact@revolutionmortgage.com'}
                width={257}
              />
            </Box>

            {/* Row 2: Website and Address */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: theme.unit(7.5),
              }}
            >
              <InfoField
                label="Website"
                value={displayWebsiteUrl}
                copyValue={fullWebsiteUrl}
                isLink
                width={280}
              />
              <AddressField
                label="Address"
                line1={addressLine1}
                line2={addressLine2}
                width={257}
              />
            </Box>
          </Box>
        </ColumnLayout>
      </Box>
    </MuiModal>
  );
};

/**
 * Render ContactModal using configuration
 * This is the main entry point for rendering the modal
 */
export const renderContactModal = (
  params: ContactModalConfigParams
): React.ReactNode => {
  return getContactModalConfig(params);
};
